import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type { EmployeeRef } from '../api/types';
import { useApp } from '../state/AppContext';
import { EmployeeName, EmptyRow, LocationSelect, SortableHeader, formatDate, today } from '../components/ui';

/**
 * The raw punch rows, read-only.
 *
 * Daily Summary shows the aggregate — first in, last out — which hides the
 * three things you actually come here to check: several punches in one day, a
 * day with no punch at all (which is what an absence is, so there is no daily
 * record to open), and punches held because their employee is not loaded.
 *
 * Nothing is editable. Punch data comes from the clock; correcting it means
 * correcting the source and re-importing.
 */

interface PunchRow {
  punchId: number;
  employee: EmployeeRef;
  workDate: string;
  clockIn: string | null;
  clockOut: string | null;
  payCode: string | null;
  workedMinutes: number | null;
  importBatchId: number | null;
  unmatched: boolean;
}

interface PunchResponse {
  rows: PunchRow[];
  window: { from: string; to: string; label: string };
  totalCount: number;
  unmatchedCount: number;
  daysWithMultiplePunches: number;
}

export function PunchData() {
  const { scopedDepartments, scopedFacilities, locationsFor } = useApp();

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [department, setDepartment] = useState('all');
  const [location, setLocation] = useState('all');
  const [unmatchedOnly, setUnmatchedOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ column: string; dir: 'asc' | 'desc' }>(
    { column: 'workDate', dir: 'desc' });

  const { data, isLoading } = useQuery({
    queryKey: ['punches', from, to, department, location, unmatchedOnly],
    queryFn: () => api.get<PunchResponse>('/api/punches', {
      from, to, department, location, unmatchedOnly, take: 5000,
    }),
  });

  const toggleSort = (column: string) =>
    setSort((s) => ({ column, dir: s.column === column && s.dir === 'asc' ? 'desc' : 'asc' }));

  const q = search.trim().toLowerCase();

  const rows = useMemo(() => {
    const list = (data?.rows ?? []).filter((r) =>
      q === '' || r.employee.name.toLowerCase().includes(q) || String(r.employee.employeeId).includes(q));

    // Times arrive formatted for display ("7:55 AM"), which does not sort, so
    // sorting keys off minutes-since-midnight instead of the label.
    const minutes = (t: string | null) => {
      if (!t) return -1;
      const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(t.trim());
      if (!m) return -1;
      let h = Number(m[1]) % 12;
      if (m[3].toUpperCase() === 'PM') h += 12;
      return h * 60 + Number(m[2]);
    };

    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      switch (sort.column) {
        case 'employee': return a.employee.name.localeCompare(b.employee.name) * dir;
        case 'department': return (a.employee.department ?? '').localeCompare(b.employee.department ?? '') * dir;
        case 'clockIn': return (minutes(a.clockIn) - minutes(b.clockIn)) * dir;
        case 'clockOut': return (minutes(a.clockOut) - minutes(b.clockOut)) * dir;
        case 'hours': return ((a.workedMinutes ?? -1) - (b.workedMinutes ?? -1)) * dir;
        case 'payCode': return (a.payCode ?? '').localeCompare(b.payCode ?? '') * dir;
        case 'batch': return ((a.importBatchId ?? 0) - (b.importBatchId ?? 0)) * dir;
        default: return a.workDate.localeCompare(b.workDate) * dir
          || a.employee.name.localeCompare(b.employee.name);
      }
    });
  }, [data, q, sort]);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Punch Data</div>
          <div className="page-subtitle">
            Raw clock records as imported · {data?.window.label ?? '…'} · read-only
          </div>
        </div>
      </div>

      {(data?.unmatchedCount ?? 0) > 0 && !unmatchedOnly && (
        <div className="banner banner--conflict" style={{ marginBottom: 14 }}>
          <div>
            <strong>{data!.unmatchedCount} punch rows have no employee record.</strong>{' '}
            They imported and are held — they will count as soon as that employee exists.
          </div>
          <button className="btn btn--sm" onClick={() => setUnmatchedOnly(true)}>Show only these</button>
        </div>
      )}

      <div className="filter-row">
        <input className="control" style={{ maxWidth: 240 }} placeholder="Search name or employee ID"
          value={search} onChange={(e) => setSearch(e.target.value)} />
        <input className="control" type="date" value={from} max={to || today()}
          onChange={(e) => setFrom(e.target.value)} aria-label="From" />
        <input className="control" type="date" value={to} min={from}
          onChange={(e) => setTo(e.target.value)} aria-label="To" />
        <select className="control" style={{ maxWidth: 220 }} value={department}
          onChange={(e) => setDepartment(e.target.value)} aria-label="Department">
          <option value="all">All departments</option>
          <option value="__none__">— No department set —</option>
          {scopedDepartments.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <LocationSelect facilities={scopedFacilities} allowed={locationsFor('dailySummary', 'view')}
          value={location} onChange={setLocation} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, color: 'var(--text-secondary)' }}>
          <input type="checkbox" checked={unmatchedOnly} onChange={(e) => setUnmatchedOnly(e.target.checked)} />
          Unmatched only
        </label>
        <div className="meta">{rows.length} rows</div>
      </div>

      {(data?.daysWithMultiplePunches ?? 0) > 0 && (
        <div className="meta" style={{ marginBottom: 10 }}>
          {data!.daysWithMultiplePunches} employee-days have more than one punch row. That is normal — a lunch break
          is two pairs — but a day with double its usual pairs is what a file imported twice looks like.
        </div>
      )}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <SortableHeader label="DATE" column="workDate" sort={sort} onSort={toggleSort} />
              <th className="num">ID</th>
              <SortableHeader label="EMPLOYEE" column="employee" sort={sort} onSort={toggleSort} />
              <SortableHeader label="DEPARTMENT" column="department" sort={sort} onSort={toggleSort} />
              <SortableHeader label="CLOCK IN" column="clockIn" sort={sort} onSort={toggleSort} />
              <SortableHeader label="CLOCK OUT" column="clockOut" sort={sort} onSort={toggleSort} />
              <SortableHeader label="HOURS" column="hours" sort={sort} onSort={toggleSort} className="num" />
              <SortableHeader label="PAY CODE" column="payCode" sort={sort} onSort={toggleSort} />
              <SortableHeader label="BATCH" column="batch" sort={sort} onSort={toggleSort} className="num" />
            </tr>
          </thead>
          <tbody>
            {isLoading && <EmptyRow colSpan={9}>Loading punch data…</EmptyRow>}
            {!isLoading && rows.length === 0 && (
              <EmptyRow colSpan={9}>
                {unmatchedOnly ? 'No unmatched punch rows.' : 'No punch rows for these filters.'}
              </EmptyRow>
            )}
            {rows.map((r) => (
              <tr key={r.punchId} style={r.unmatched ? { background: 'var(--orange-tint)' } : undefined}>
                <td className="strong">{formatDate(r.workDate)}</td>
                <td className="num">{r.employee.employeeId}</td>
                <td><EmployeeName employee={r.employee} /></td>
                <td>{r.employee.department ?? '—'}</td>
                <td>{r.clockIn ?? <span style={{ color: 'var(--muted)' }}>none</span>}</td>
                <td>{r.clockOut ?? <span style={{ color: 'var(--muted)' }}>none</span>}</td>
                <td className="num">
                  {r.workedMinutes != null ? (r.workedMinutes / 60).toFixed(2) : '—'}
                </td>
                <td>{r.payCode ?? '—'}</td>
                <td className="num meta">{r.importBatchId ?? 'seed'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
